<template>
  <div class="sketch-entities-transform-property" data-testid="sketch-rotate-entities-property-section">
    <n-collapse :default-expanded-names="['entities', 'params']" class="pm-collapse">
      <n-collapse-item title="要旋转的实体(E)" name="entities">
        <div class="section-body">
          <div
            class="selection-box"
            data-testid="rotate-entities-selection-box"
            @click="activateSelection(SketchEntitiesTransformSelectionRole.Entities)"
          >
            <SelectionListFrame
              :class="{ 'selection-list-frame--active': isEntitiesSelectionActive }"
              :items="selectedItems"
              :active-index="isEntitiesSelectionActive ? 0 : -1"
              :item-key-field="'id'"
              :item-label-field="'label'"
              :visible-rows="4"
              :context-menu-enabled="true"
              :show-delete-all-action="true"
              :show-delete-action="true"
              empty-text="请在视口中选择要旋转的实体"
              @delete-item="handleDeleteItem"
              @delete-all="handleDeleteAll"
            >
              <template #item="{ item }">
                <div class="entity-main">
                  <n-text>{{ resolveSelectionItemLabel(item) }}</n-text>
                </div>
              </template>
            </SelectionListFrame>
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数(P)" name="params">
        <div class="section-body">
          <div class="selection-row">
            <span class="field-label">旋转中心:</span>
            <div
              class="reference-list"
              data-testid="rotate-center-point-box"
              @click="activateSelection(SketchEntitiesTransformSelectionRole.RotationCenter)"
            >
              <SelectionListFrame
                :class="{
                  'selection-list-frame--single-row': true,
                  'selection-list-frame--active': isRotationCenterSelectionActive,
                }"
                :items="rotationCenterItems"
                :active-index="isRotationCenterSelectionActive ? 0 : -1"
                :visible-rows="1"
                :context-menu-enabled="false"
                :show-delete-action="false"
                :show-delete-all-action="false"
                :item-key-field="'id'"
                :item-label-field="'label'"
                empty-text="旋转所定义的点"
                list-role="list"
              >
                <template #item="{ item }">
                  <div class="entity-main">
                    <n-text>{{ resolveSelectionItemLabel(item) }}</n-text>
                  </div>
                </template>
              </SelectionListFrame>
            </div>
          </div>

          <div class="coordinate-row">
            <span class="coordinate-label">角度</span>
            <n-input-number
              ref="angleInputRef"
              :value="state.angleDeg"
              size="small"
              :precision="3"
              :step="1"
              @update:value="updateAngle"
            />
            <span class="unit-label">度</span>
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import { NCollapse, NCollapseItem, NInputNumber, NText } from 'naive-ui';
import type { InputNumberInst } from 'naive-ui';
import {
  SketchEntitiesTransformCommand,
  SketchEntitiesTransformInputMode,
  SketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole,
  type PropertyManagerContext,
  type SketchEntitiesTransformState,
} from '@/core';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import { useViewer } from '@/hooks';
import {
  SketchEntitiesTransformRelationRetentionMode as CadSketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole as CadSketchEntitiesTransformSelectionRole,
  type SketchEntitiesTransformControllerSnapshot,
  type SketchRotateEntitiesController,
} from '@puppycad/cad-core';

/**
 * 旋转实体属性面板参数。
 */
interface SketchRotateEntitiesPropertySectionProps {
  /** PropertyManager 上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<SketchRotateEntitiesPropertySectionProps>();
const { getViewer, setViewerCallback } = useViewer();

/** 旋转角度输入框显示小数位数，与其它旋转类 PropertyManager 保持一致。 */
const ANGLE_INPUT_PRECISION = 3;

/**
 * 创建旋转实体面板默认状态。
 *
 * @returns 旋转实体面板默认状态。
 */
function createDefaultState(): SketchEntitiesTransformState {
  return {
    active: false,
    command: SketchEntitiesTransformCommand.Rotate,
    inputMode: SketchEntitiesTransformInputMode.FromTo,
    activeSelectionRole: SketchEntitiesTransformSelectionRole.Entities,
    relationRetentionMode: SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations,
    selectedItems: [],
    fromPoint: null,
    toPoint: null,
    rotationCenter: null,
    targetX: null,
    targetY: null,
    angleDeg: 0,
    canConfirm: false,
    canRepeat: false,
  };
}

/** 当前面板本地状态。 */
const localState = ref<SketchEntitiesTransformState>(createDefaultState());

/** 控制器快照订阅。 */
let snapshotSubscription: Subscription | null = null;

/** Viewer 就绪回调取消函数。 */
let disposeViewerReadyCallback: (() => void) | null = null;

/** 当前旋转实体命令状态。 */
const state = computed(() => localState.value);

/** 角度数字输入框实例，用于旋转中心确认后承接键盘输入。 */
const angleInputRef = ref<InputNumberInst | null>(null);

/** 当前已选实体列表。 */
const selectedItems = computed(() => state.value.selectedItems ?? []);

/** 当前旋转中心列表项。 */
const rotationCenterItems = computed(() => state.value.rotationCenter ? [state.value.rotationCenter] : []);

/** 当前“要旋转的实体”输入框是否激活。 */
const isEntitiesSelectionActive = computed<boolean>(() => (
  state.value.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
));

/** 当前“旋转中心”输入框是否激活。 */
const isRotationCenterSelectionActive = computed<boolean>(() => (
  state.value.activeSelectionRole === SketchEntitiesTransformSelectionRole.RotationCenter
));

/**
 * 解析选择列表项显示文本。
 *
 * @param item SelectionListFrame 插槽项。
 * @returns 可显示的标签文本。
 */
function resolveSelectionItemLabel(item: unknown): string {
  if (typeof item !== 'object' || item === null || !('label' in item)) {
    return '';
  }

  const label = (item as { label?: unknown }).label;
  return typeof label === 'string' ? label : '';
}

/**
 * 获取旋转实体控制器。
 *
 * @returns 控制器实例。
 */
function getRotateEntitiesController(): SketchRotateEntitiesController | null {
  return getViewer()?.sketchRotateEntitiesController ?? null;
}

/**
 * 将 cad-core 选择角色映射为 PropertyManager 选择角色。
 *
 * @param role cad-core 选择角色。
 * @returns PropertyManager 选择角色。
 */
function mapSelectionRoleFromCad(
  role: CadSketchEntitiesTransformSelectionRole,
): SketchEntitiesTransformState['activeSelectionRole'] {
  if (role === CadSketchEntitiesTransformSelectionRole.None) {
    return null;
  }
  if (role === CadSketchEntitiesTransformSelectionRole.RotationCenter) {
    return SketchEntitiesTransformSelectionRole.RotationCenter;
  }
  return SketchEntitiesTransformSelectionRole.Entities;
}

/**
 * 将控制器快照映射为面板状态。
 *
 * @param snapshot 控制器快照。
 * @returns 面板状态。
 */
function mapSnapshotToState(snapshot: SketchEntitiesTransformControllerSnapshot): SketchEntitiesTransformState {
  return {
    active: snapshot.active,
    command: snapshot.command,
    inputMode: SketchEntitiesTransformInputMode.FromTo,
    activeSelectionRole: mapSelectionRoleFromCad(snapshot.activeSelectionRole),
    relationRetentionMode: snapshot.relationRetentionMode === CadSketchEntitiesTransformRelationRetentionMode.BreakExternalRelations
      ? SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations
      : SketchEntitiesTransformRelationRetentionMode.KeepRelations,
    selectedItems: snapshot.selectedItems.map((item) => ({
      id: item.id,
      label: item.label,
    })),
    fromPoint: null,
    toPoint: null,
    rotationCenter: snapshot.rotationCenter
      ? {
        id: snapshot.rotationCenter.id,
        label: snapshot.rotationCenter.label,
      }
      : null,
    targetX: null,
    targetY: null,
    // cad-core 保留完整拖动精度；UI 主动截断受控值，避免 Naive UI 在精度溢出时显示完整浮点尾数。
    angleDeg: snapshot.angleDeg === null || !Number.isFinite(snapshot.angleDeg)
      ? null
      : Number(snapshot.angleDeg.toFixed(ANGLE_INPUT_PRECISION)),
    canConfirm: snapshot.canConfirm,
    canRepeat: snapshot.canRepeat,
  };
}

/**
 * 从旋转实体控制器同步最新快照到本地状态。
 */
function syncFromController(): void {
  const snapshot = getRotateEntitiesController()?.getSnapshot?.() ?? null;
  if (!snapshot) {
    localState.value = props.context.sketchEntitiesTransformState ?? createDefaultState();
    props.context.syncSketchEntitiesTransformState?.(localState.value);
    return;
  }

  localState.value = mapSnapshotToState(snapshot);
  props.context.syncSketchEntitiesTransformState?.(localState.value);
}

/**
 * 建立旋转实体控制器快照订阅。
 */
function bindControllerSnapshot(): void {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = getViewer()?.observeSketchRotateEntitiesSnapshot?.().subscribe((snapshot) => {
    const wasActive = localState.value.active;
    const hadRotationCenter = localState.value.rotationCenter !== null;
    localState.value = mapSnapshotToState(snapshot);
    props.context.syncSketchEntitiesTransformState?.(localState.value);
    if (!hadRotationCenter && snapshot.rotationCenter) {
      // 中心确认后的下一项是角度，等待 DOM 更新后直接承接键盘输入并全选旧值。
      void focusAngleInput();
    }
    //------ 旋转实体面板退出同步 开始------
    /**
     * 旋转确认由 cad-core 清理命令态，面板需要同步退出宿主工作流。
     */
    if (wasActive && !snapshot.active) {
      props.context.cancelSketchEntitiesTransform?.();
    }
    //------ 旋转实体面板退出同步 结束------
  }) ?? null;
}

/**
 * 聚焦并全选角度输入框。
 *
 * @returns 输入框完成聚焦后的 Promise。
 *
 * 设计说明：
 * 1. 控制器快照先结束旋转中心取点，面板随后把键盘焦点交给角度字段；
 * 2. 等待 nextTick 可避免状态同步期间输入组件尚未完成更新；
 * 3. 全选当前数值后，用户直接键入即可替换默认角度，符合连续参数输入流程。
 */
async function focusAngleInput(): Promise<void> {
  await nextTick();
  angleInputRef.value?.focus();
  angleInputRef.value?.select();
}

onMounted(() => {
  syncFromController();
  bindControllerSnapshot();
  disposeViewerReadyCallback = setViewerCallback(() => {
    syncFromController();
    bindControllerSnapshot();
  });
});

onBeforeUnmount(() => {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = null;
  disposeViewerReadyCallback?.();
  disposeViewerReadyCallback = null;
});

/**
 * 激活指定选择角色。
 *
 * @param role 目标选择角色。
 */
function activateSelection(role: SketchEntitiesTransformSelectionRole): void {
  getRotateEntitiesController()?.setActiveSelectionRole?.(role);
  syncFromController();
}

/**
 * 更新旋转角度。
 *
 * @param value 角度值。
 */
function updateAngle(value: number | null): void {
  getRotateEntitiesController()?.setAngleDeg?.(value);
  syncFromController();
}

/**
 * 处理删除单个实体项。
 *
 * @param payload SelectionListFrame delete-item 事件。
 */
function handleDeleteItem(payload: { item: unknown; index: number }): void {
  const item = payload.item as { id: string };
  if (item?.id) {
    getRotateEntitiesController()?.removeSelectedEntity?.(item.id);
    syncFromController();
  }
}

/**
 * 处理删除全部实体项。
 */
function handleDeleteAll(): void {
  for (const item of selectedItems.value) {
    getRotateEntitiesController()?.removeSelectedEntity?.(item.id);
  }
  syncFromController();
}

</script>

<style scoped>
.sketch-entities-transform-property {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selection-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.selection-box {
  cursor: pointer;
}

.entity-main {
  display: flex;
  min-width: 0;
  align-items: center;
}

.reference-list {
  min-width: 0;
  cursor: pointer;
}

.reference-list :deep(.selection-list-frame--single-row) {
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.reference-list :deep(.selection-list-frame--single-row::-webkit-scrollbar) {
  width: 0;
  height: 0;
}

.selection-box :deep(.selection-list-frame--active),
.reference-list :deep(.selection-list-frame--active) {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.field-label,
.coordinate-label,
.unit-label {
  color: var(--n-text-color-2);
  font-size: 12px;
}

.coordinate-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 6px;
}
</style>
